import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { addDays, startOfDay } from 'date-fns';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { ContentLayout, StepperCard } from '@ibk/shared-layout';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase, ReleaseType } from '@ibk/shared-types';
import { Calendar, Icon, PayrollDeductionSection } from '@ibk/shared-ui';
import { formatCurrency } from '@ibk/shared-utils';
import { ReleaseTypeSection } from '../../../../components/ReleaseTypeSection';
import { useEmissorFlow } from '../../../../hooks';
import { useBusinessDays } from '../../../../hooks/useBusinessDays';
import { DEFAULT_CURRENCY_VALUE } from '../../../../utils';
import { getUnformatedValue, isActiveStatus, shouldUpdateBeneficiaries, updateBeneficiariesDiscounts, updateBeneficiariesValues, } from '../../../../utils/beneficiaryUtils';
import { isZeroOrEmptyCurrency, isZeroOrEmptyDays } from '../../../../utils/validationUtils';
import { NewRechargeStep, NewRechargeStepStatus } from '../../types';
import { NewRechargeSidebar } from '../NewRechargeSidebar';
import styles from './GlobalSettings.module.css';
import { GlobalSettingsNew } from './GlobalSettingsNew';
function resolveGlobalValueForReleaseType(product, releaseType, currentGlobalValue) {
    if (!product) {
        return currentGlobalValue;
    }
    if (releaseType === ReleaseType.MONTHLY) {
        return product.baseValue ? formatCurrency(product.baseValue) : DEFAULT_CURRENCY_VALUE;
    }
    if (releaseType === ReleaseType.DAILY) {
        return product.amountDaily ? formatCurrency(product.amountDaily) : DEFAULT_CURRENCY_VALUE;
    }
    return currentGlobalValue;
}
function GlobalSettingsLegacy({ currentStep, formData, isLoading = false, onComplete, onReturn, onStepChange, onSelectCreditDate, onPayrollDeductionChange, onDiscountTypeChange, onDiscountValueChange, onIsIndividualDiscountChange, onUpdateBeneficiariesData, products = [], headerStepper, }) {
    const { t, currentLanguage } = useTranslation();
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isBeneficiaryDiscountEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_ENABLE_BENEFICIARY_DISCOUNT);
    const [showCalendar, setShowCalendar] = useState(false);
    const [globalValueError, setGlobalValueError] = useState(undefined);
    const [businessDaysError, setBusinessDaysError] = useState(undefined);
    const breadcrumbs = [
        { label: t('recharge.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('recharge.breadcrumb.new-recharge'), isActive: true },
    ];
    const currentStepConfig = {
        id: NewRechargeStep.GLOBAL_SETTINGS,
        title: t('recharge.step.global-settings'),
        number: 2,
        status: NewRechargeStepStatus.ACTIVE,
    };
    const handleSelectDate = useCallback((date) => {
        const startDay = startOfDay(new Date());
        const [, timezone] = startDay.toISOString().split('T');
        const dateWithTimezone = date ? new Date(`${date}T${timezone}`) : undefined;
        onSelectCreditDate(dateWithTimezone);
    }, [onSelectCreditDate]);
    const handleBack = useCallback(() => {
        onReturn();
    }, [onReturn]);
    const handleNext = useCallback(async () => {
        if (formData.releaseType) {
            const zeroValueErrorMessage = t('parameters.step2.validation.zero-value');
            let hasError = false;
            if (isZeroOrEmptyCurrency(formData.globalValue)) {
                setGlobalValueError(zeroValueErrorMessage);
                hasError = true;
            }
            if (formData.releaseType === ReleaseType.DAILY && isZeroOrEmptyDays(formData.businessDays)) {
                setBusinessDaysError(zeroValueErrorMessage);
                hasError = true;
            }
            if (hasError)
                return;
        }
        onComplete(true);
    }, [onComplete, formData.globalValue, formData.releaseType, formData.businessDays, t]);
    const isValidForm = () => {
        return Boolean(formData.creditDate);
    };
    const formatDate = (date) => {
        return date
            ? date.toLocaleDateString(currentLanguage, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            })
            : '';
    };
    const formatDateForDateISO = (date) => {
        if (!date)
            return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    const calendarInitialDate = formatDateForDateISO(formData.creditDate ?? new Date());
    const calendarMinDate = formatDateForDateISO(new Date());
    const calendarMaxDate = formatDateForDateISO(addDays(new Date(), 180));
    const competenceString = formData.competenceDate
        ? `${formData.competenceDate.getFullYear()}-${String(formData.competenceDate.getMonth() + 1).padStart(2, '0')}`
        : undefined;
    const { businessDays: businessDaysData, isLoading: isLoadingBusinessDays } = useBusinessDays({
        competence: competenceString,
        enabled: !!competenceString && formData.releaseType === ReleaseType.DAILY,
    });
    useEffect(() => {
        if (businessDaysData !== null &&
            businessDaysData !== undefined &&
            formData.businessDays === undefined &&
            onUpdateBeneficiariesData &&
            !isLoadingBusinessDays) {
            onUpdateBeneficiariesData({ businessDays: businessDaysData });
        }
    }, [businessDaysData, formData.businessDays, onUpdateBeneficiariesData, isLoadingBusinessDays]);
    const checkShouldUpdateBeneficiaries = useCallback((releaseType, globalValue) => {
        return shouldUpdateBeneficiaries(releaseType, globalValue, formData.beneficiaries, DEFAULT_CURRENCY_VALUE);
    }, [formData.beneficiaries]);
    const handleReleaseTypeChange = useCallback((value) => {
        if (!onUpdateBeneficiariesData) {
            return;
        }
        const releaseType = value;
        const product = products.find(product => product.voucherTypeDescription === formData.voucherType);
        const globalValue = resolveGlobalValueForReleaseType(product, releaseType, formData.globalValue ?? DEFAULT_CURRENCY_VALUE);
        const updateData = {
            releaseType,
            globalValue,
            amountDaily: releaseType === ReleaseType.DAILY ? getUnformatedValue(globalValue) : undefined,
        };
        if (checkShouldUpdateBeneficiaries(releaseType, globalValue) && formData.beneficiaries) {
            const globalNumericValue = getUnformatedValue(globalValue);
            const currentBusinessDays = releaseType === ReleaseType.DAILY ? (formData.businessDays ?? 0) : 0;
            const updatedBeneficiaries = updateBeneficiariesValues(formData.beneficiaries, globalNumericValue, releaseType, currentBusinessDays);
            updateData.beneficiaries = updatedBeneficiaries;
        }
        onUpdateBeneficiariesData(updateData);
    }, [
        onUpdateBeneficiariesData,
        products,
        formData.voucherType,
        formData.globalValue,
        formData.businessDays,
        formData.beneficiaries,
        checkShouldUpdateBeneficiaries,
        updateBeneficiariesValues,
    ]);
    const handleGlobalValueChange = useCallback((value) => {
        if (!isZeroOrEmptyCurrency(value)) {
            setGlobalValueError(undefined);
        }
        if (!onUpdateBeneficiariesData) {
            return;
        }
        const updateData = {
            globalValue: value,
            amountDaily: formData.releaseType === ReleaseType.DAILY ? getUnformatedValue(value) : undefined,
        };
        if (checkShouldUpdateBeneficiaries(formData.releaseType, value) &&
            formData.beneficiaries &&
            formData.releaseType) {
            const globalNumericValue = getUnformatedValue(value);
            const currentBusinessDays = formData.releaseType === ReleaseType.DAILY ? (formData.businessDays ?? 0) : 0;
            const updatedBeneficiaries = updateBeneficiariesValues(formData.beneficiaries, globalNumericValue, formData.releaseType, currentBusinessDays);
            updateData.beneficiaries = updatedBeneficiaries;
        }
        onUpdateBeneficiariesData(updateData);
    }, [
        onUpdateBeneficiariesData,
        formData.releaseType,
        formData.businessDays,
        formData.beneficiaries,
        checkShouldUpdateBeneficiaries,
        updateBeneficiariesValues,
    ]);
    const handleBusinessDaysChange = useCallback((value) => {
        if (value > 0) {
            setBusinessDaysError(undefined);
        }
        if (!onUpdateBeneficiariesData) {
            return;
        }
        const updateData = { businessDays: value };
        if (checkShouldUpdateBeneficiaries(formData.releaseType, formData.globalValue) &&
            formData.releaseType === ReleaseType.DAILY &&
            formData.globalValue &&
            formData.beneficiaries &&
            value > 0) {
            const globalNumericValue = getUnformatedValue(formData.globalValue);
            const updatedBeneficiaries = formData.beneficiaries.map(beneficiary => {
                if (isActiveStatus(beneficiary.statusInProduct)) {
                    const totalValue = globalNumericValue * value;
                    return {
                        ...beneficiary,
                        productValue: totalValue,
                        productDaysQuantity: value,
                    };
                }
                return beneficiary;
            });
            updateData.beneficiaries = updatedBeneficiaries;
        }
        onUpdateBeneficiariesData(updateData);
    }, [
        onUpdateBeneficiariesData,
        formData.releaseType,
        formData.globalValue,
        formData.beneficiaries,
        checkShouldUpdateBeneficiaries,
    ]);
    const handlePayrollDeductionChange = useCallback((value) => {
        onPayrollDeductionChange(value);
        if (!onUpdateBeneficiariesData || !formData.beneficiaries) {
            return;
        }
        const updatedBeneficiaries = updateBeneficiariesDiscounts(formData.beneficiaries, formData.discountValue, isBeneficiaryDiscountEnabled, value);
        onUpdateBeneficiariesData({
            beneficiaries: updatedBeneficiaries,
        });
    }, [
        onPayrollDeductionChange,
        onUpdateBeneficiariesData,
        formData.beneficiaries,
        formData.discountValue,
        isBeneficiaryDiscountEnabled,
    ]);
    const handleDiscountTypeChange = useCallback((value) => {
        onDiscountTypeChange(value);
        if (!onUpdateBeneficiariesData || !formData.beneficiaries) {
            return;
        }
        const updatedBeneficiaries = updateBeneficiariesDiscounts(formData.beneficiaries, formData.discountValue, isBeneficiaryDiscountEnabled, formData.payrollDeduction ?? false);
        onUpdateBeneficiariesData({ beneficiaries: updatedBeneficiaries });
    }, [
        onDiscountTypeChange,
        onUpdateBeneficiariesData,
        formData.beneficiaries,
        formData.discountValue,
        formData.payrollDeduction,
        isBeneficiaryDiscountEnabled,
    ]);
    const handleDiscountValueChange = useCallback((value) => {
        onDiscountValueChange(value);
        if (!onUpdateBeneficiariesData || !formData.beneficiaries) {
            return;
        }
        const updatedBeneficiaries = updateBeneficiariesDiscounts(formData.beneficiaries, value, isBeneficiaryDiscountEnabled, formData.payrollDeduction ?? false);
        onUpdateBeneficiariesData({ beneficiaries: updatedBeneficiaries });
    }, [
        onDiscountValueChange,
        onUpdateBeneficiariesData,
        formData.beneficiaries,
        formData.payrollDeduction,
        isBeneficiaryDiscountEnabled,
        updateBeneficiariesDiscounts,
    ]);
    const handleIsIndividualDiscountChange = useCallback((value) => {
        if (!onIsIndividualDiscountChange) {
            return;
        }
        onIsIndividualDiscountChange(value);
        if (!onUpdateBeneficiariesData || !formData.beneficiaries || formData.beneficiaries.length === 0) {
            return;
        }
        const updatedBeneficiaries = updateBeneficiariesDiscounts(formData.beneficiaries, formData.discountValue, isBeneficiaryDiscountEnabled, value);
        onUpdateBeneficiariesData({
            beneficiaries: updatedBeneficiaries,
            isIndividualDiscount: value,
        });
    }, [
        onIsIndividualDiscountChange,
        onUpdateBeneficiariesData,
        formData.beneficiaries,
        formData.discountValue,
        isBeneficiaryDiscountEnabled,
        updateBeneficiariesDiscounts,
    ]);
    const renderMainCard = () => (_jsxs(StepperCard, { currentStep: currentStepConfig, showStepper: !headerStepper, actions: {
            back: {
                label: t('recharge.button.back'),
                onClick: handleBack,
            },
            next: {
                label: t('recharge.button.next'),
                onClick: handleNext,
                loading: isLoading,
                disabled: isLoading || !isValidForm(),
            },
        }, children: [headerStepper, _jsxs("div", { className: styles.stepContent, "data-testid": 'viewNewRechargeGlobalSettingsStepContent', children: [_jsxs("div", { className: styles.formSection, "data-testid": 'viewNewRechargeCompetenceStepContent', children: [_jsx("h3", { className: styles.title, children: t('recharge.step.global-settings.title.release-credit') }), _jsxs("div", { className: styles.textInputAndSvg, children: [_jsx("input", { className: styles.textInput, type: 'text', onFocus: () => setShowCalendar(true), readOnly: true, value: formatDate(formData.creditDate), id: 'selectDateSingleValue', "data-testid": 'selectDateSingleValue' }), _jsx(Icon, { name: 'calendar', className: styles.calendarIcon, onClick: () => setShowCalendar(!showCalendar) })] }), _jsx(Calendar, { dataTestId: 'selectDateSingleValue', onlyCalendar: true, selectionType: 'single', select: handleSelectDate, open: showCalendar, close: () => setShowCalendar(false), automaticClosable: true, minDate: calendarMinDate, maxDate: calendarMaxDate, initialDate: calendarInitialDate }), _jsx("span", { className: styles.textInfo, children: t('recharge.step.global-settings.info.release-credit') })] }), onUpdateBeneficiariesData && formData.releaseType && (_jsx(ReleaseTypeSection, { releaseType: formData.releaseType, globalValue: formData.globalValue, onReleaseTypeChange: handleReleaseTypeChange, onGlobalValueChange: handleGlobalValueChange, businessDays: formData.businessDays, onBusinessDaysChange: handleBusinessDaysChange, testIdIdentifier: 'NewRechargeGlobalSettings', globalValueError: globalValueError, businessDaysError: businessDaysError })), _jsx(PayrollDeductionSection, { payrollDeduction: formData.payrollDeduction, discountType: formData.discountType, discountValue: formData.discountValue, onPayrollDeductionChange: handlePayrollDeductionChange, onDiscountTypeChange: handleDiscountTypeChange, onDiscountValueChange: handleDiscountValueChange })] })] }));
    const renderSummaryCard = () => (_jsx(NewRechargeSidebar, { currentStep: currentStep, formData: formData, onStepChange: onStepChange }));
    return (_jsx(ContentLayout, { className: styles.contentLayoutGlobalSettings, breadcrumbs: breadcrumbs, sidebar: renderSummaryCard(), children: renderMainCard() }));
}
export function GlobalSettings(props) {
    const { isEmissorFlow } = useEmissorFlow();
    if (isEmissorFlow) {
        return _jsx(GlobalSettingsNew, { ...props });
    }
    return _jsx(GlobalSettingsLegacy, { ...props });
}
